// components/ExistingSiteConditions.jsx - Document existing damage and conditions
import React, { useState } from 'react';
import CameraCapture from './CameraCapture';
import PhotoAnnotator from './PhotoAnnotator';
import PhotoViewer from './PhotoViewer';

const ExistingSiteConditions = ({ data, onChange }) => {
  const [showCamera, setShowCamera] = useState(false);
  const [annotatingIndex, setAnnotatingIndex] = useState(null);

  // Initialize data structure if needed
  const conditions = data || {
    photos: [],
    notes: ''
  };

  // Update parent
  const updateConditions = (updates) => {
    onChange({ ...conditions, ...updates });
  };

  // Photo handlers
  const handlePhotoCapture = (photo) => {
    const newPhotos = [...conditions.photos, photo];
    updateConditions({ photos: newPhotos });
  };

  const handlePhotoDelete = (index) => {
    const newPhotos = conditions.photos.filter((_, i) => i !== index);
    updateConditions({ photos: newPhotos });
  };

  const handlePhotoAnnotate = (index) => {
    setAnnotatingIndex(index);
  };

  const handleAnnotationSave = (annotatedPhoto) => {
    const newPhotos = [...conditions.photos];
    newPhotos[annotatingIndex] = annotatedPhoto;
    updateConditions({ photos: newPhotos });
    setAnnotatingIndex(null);
  };

  const handlePhotoReorder = (newPhotos) => {
    updateConditions({ photos: newPhotos });
  };

  return (
    <div style={{ marginBottom: '24px' }}>
      <h2 style={{ 
        fontSize: '18px', 
        fontWeight: '600', 
        marginBottom: '16px',
        display: 'flex',
        alignItems: 'center',
        gap: '8px'
      }}>
        📸 Existing Site Conditions
      </h2>

      <p style={{ 
        fontSize: '14px', 
        color: '#5D757E', 
        marginBottom: '16px' 
      }}>
        Document any existing damage or conditions before starting work. 
        This protects both you and the client.
      </p>

      {/* Photo Section */}
      <div style={{
        backgroundColor: '#F1F0E9',
        borderRadius: '8px',
        padding: '16px',
        marginBottom: '16px'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '12px'
        }}>
          <span style={{ fontWeight: '500' }}>
            Photos ({conditions.photos.length}/5)
          </span>
          <button
            onClick={() => setShowCamera(true)}
            disabled={conditions.photos.length >= 5}
            style={{
              padding: '8px 16px',
              backgroundColor: conditions.photos.length >= 5 ? '#DADFE2' : '#388523',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: conditions.photos.length >= 5 ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}
          >
            📷 Add Photo
          </button>
        </div>

        {conditions.photos.length === 0 ? (
          <div style={{
            textAlign: 'center',
            padding: '24px',
            color: '#9ca3af',
            border: '2px dashed #DADFE2',
            borderRadius: '8px'
          }}>
            <div style={{ fontSize: '32px', marginBottom: '8px' }}>📷</div>
            <p>No photos yet. Tap "Add Photo" to document existing conditions.</p>
          </div>
        ) : (
          <PhotoViewer
            photos={conditions.photos}
            onDelete={handlePhotoDelete}
            onAnnotate={handlePhotoAnnotate}
            onReorder={handlePhotoReorder}
          />
        )}
      </div>

      {/* Pre-Existing Damage and Other Conditions */}
      <div style={{
        backgroundColor: '#F1F0E9',
        borderRadius: '8px',
        padding: '16px'
      }}>
        <label style={{ fontWeight: '500', display: 'block', marginBottom: '8px' }}>
          Pre-Existing Damage and Other Conditions or Notes
        </label>
        <textarea
          value={conditions.notes || ''}
          onChange={(e) => updateConditions({ notes: e.target.value })}
          placeholder="Document any pre-existing damage (cracked hardscape, lawn damage, fence issues, etc.) or other conditions to be aware of before starting work..."
          rows={4}
          style={{
            width: '100%',
            padding: '10px',
            border: '1px solid #DADFE2',
            borderRadius: '6px',
            fontSize: '14px',
            resize: 'vertical',
            boxSizing: 'border-box'
          }}
        />
      </div>

      {/* Camera Modal */}
      {showCamera && (
        <CameraCapture
          onCapture={(photo) => {
            handlePhotoCapture(photo);
            if (conditions.photos.length >= 4) {
              setShowCamera(false);
            }
          }}
          onClose={() => setShowCamera(false)}
          maxPhotos={5}
          currentCount={conditions.photos.length}
        />
      )}

      {/* Annotation Modal */}
      {annotatingIndex !== null && (
        <PhotoAnnotator
          photo={conditions.photos[annotatingIndex]}
          onSave={handleAnnotationSave}
          onCancel={() => setAnnotatingIndex(null)}
        />
      )}
    </div>
  );
};

export default ExistingSiteConditions;